import { useEffect, useState, type ReactNode } from "react";
import { Delete } from "lucide-react";
import { cx } from "./ui";
import { DAY_NAMES, dayInMask } from "@shared/time";
import { ICON_CHOICES } from "@shared/templates";
import { PIN_MIN, pressPinKey, type PinKey } from "@shared/pin";
import type { FamilyDTO, MemberDTO } from "@shared/types";

export const KID_COLORS: Record<string, { bg: string; soft: string; ring: string }> = {
  teal: { bg: "#14b8a6", soft: "color-mix(in oklab, #14b8a6 18%, var(--card))", ring: "#0d9488" },
  violet: { bg: "#8b5cf6", soft: "color-mix(in oklab, #8b5cf6 18%, var(--card))", ring: "#7c3aed" },
  rose: { bg: "#f43f5e", soft: "color-mix(in oklab, #f43f5e 16%, var(--card))", ring: "#e11d48" },
  amber: { bg: "#f59e0b", soft: "color-mix(in oklab, #f59e0b 20%, var(--card))", ring: "#d97706" },
  sky: { bg: "#0ea5e9", soft: "color-mix(in oklab, #0ea5e9 18%, var(--card))", ring: "#0284c7" },
  lime: { bg: "#84cc16", soft: "color-mix(in oklab, #84cc16 20%, var(--card))", ring: "#65a30d" },
  orange: { bg: "#f97316", soft: "color-mix(in oklab, #f97316 18%, var(--card))", ring: "#ea580c" },
  fuchsia: { bg: "#d946ef", soft: "color-mix(in oklab, #d946ef 18%, var(--card))", ring: "#c026d3" },
};

export function kidColor(color: string | undefined) {
  return KID_COLORS[color ?? "teal"] ?? KID_COLORS.teal;
}

export function Avatar({
  member,
  size = "md",
  className,
}: {
  member: Pick<MemberDTO, "avatar" | "color" | "name">;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const c = kidColor(member.color);
  const sizes = { sm: "size-8 text-lg", md: "size-11 text-2xl", lg: "size-16 text-4xl", xl: "size-24 text-6xl" };
  return (
    <span
      className={cx("inline-flex shrink-0 items-center justify-center rounded-full", sizes[size], className)}
      style={{ background: c.soft, boxShadow: `inset 0 0 0 2px ${c.bg}` }}
      aria-label={member.name}
      role="img"
    >
      <span aria-hidden>{member.avatar}</span>
    </span>
  );
}

/** Points with the family currency icon. */
export function Points({
  value,
  family,
  className,
  signed,
  showName,
}: {
  value: number;
  family: Pick<FamilyDTO, "currencyIcon" | "currencyName">;
  className?: string;
  signed?: boolean;
  showName?: boolean;
}) {
  const text = `${signed && value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toLocaleString()}`;
  return (
    <span className={cx("inline-flex items-center gap-1 font-extrabold tabular-nums", className)}>
      <span aria-hidden className="text-[0.9em]">
        {family.currencyIcon}
      </span>
      {text}
      {showName && <span className="font-bold">{family.currencyName}</span>}
    </span>
  );
}

export function ProgressRing({
  value,
  size = 64,
  stroke = 8,
  color = "var(--brand)",
  children,
}: {
  value: number;
  size?: number;
  stroke?: number;
  color?: string;
  children?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 500ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

export function ProgressBar({ value, color = "var(--brand)", className }: { value: number; color?: string; className?: string }) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div className={cx("h-3 w-full overflow-hidden rounded-full bg-sunken ring-1 ring-line/60", className)}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct * 100}%`, background: color }} />
    </div>
  );
}

/**
 * Takes up to PIN_MAX digits and submits on Go (or Enter) once PIN_MIN are in. It never
 * auto-submits, so how many digits a kid's PIN has stays hidden from the device.
 */
export function PinPad({
  onComplete,
  disabled,
  error,
}: {
  onComplete: (pin: string) => void;
  disabled?: boolean;
  error?: boolean;
}) {
  const [pin, setPin] = useState("");
  useEffect(() => {
    if (error) setPin("");
  }, [error]);
  const press = (key: PinKey) => {
    if (disabled) return;
    const next = pressPinKey(pin, key);
    setPin(next.pin);
    if (next.submit) {
      onComplete(next.pin);
      setTimeout(() => setPin(""), 400);
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key as PinKey);
      else if (e.key === "Backspace") press("delete");
      else if (e.key === "Enter") press("go");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const key = "h-16 rounded-2xl bg-card text-2xl font-extrabold shadow-card ring-1 ring-line transition active:scale-95 active:bg-sunken";
  return (
    <div className="mx-auto w-full max-w-xs">
      <div
        className={cx("mb-6 flex h-5 items-center justify-center gap-3", error && "animate-wiggle")}
        aria-label={`${pin.length} ${pin.length === 1 ? "digit" : "digits"} entered`}
      >
        {Array.from(pin).map((_, i) => (
          <span key={i} className={cx("size-4 rounded-full transition", error ? "bg-danger" : "bg-brand scale-110")} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {(["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map((d) => (
          <button key={d} type="button" onClick={() => press(d)} disabled={disabled} className={key}>
            {d}
          </button>
        ))}
        <button
          type="button"
          aria-label="Delete"
          onClick={() => press("delete")}
          className="flex h-16 items-center justify-center rounded-2xl text-muted transition active:scale-95"
        >
          <Delete className="size-7" />
        </button>
        <button type="button" onClick={() => press("0")} disabled={disabled} className={key}>
          0
        </button>
        <button
          type="button"
          onClick={() => press("go")}
          disabled={disabled || pin.length < PIN_MIN}
          className="h-16 rounded-2xl bg-brand text-2xl font-extrabold text-on-brand shadow-card transition active:scale-95 disabled:opacity-40"
        >
          Go
        </button>
      </div>
    </div>
  );
}

export function IconPicker({
  value,
  onChange,
  choices = ICON_CHOICES,
}: {
  value: string;
  onChange: (v: string) => void;
  choices?: string[];
}) {
  return (
    <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 py-1">
      {choices.map((icon) => (
        <button
          key={icon}
          type="button"
          onClick={() => onChange(icon)}
          aria-label={`Icon ${icon}`}
          aria-pressed={value === icon}
          className={cx(
            "flex size-11 shrink-0 items-center justify-center rounded-2xl text-2xl transition",
            value === icon ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken hover:bg-line/60",
          )}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

export function KidChips({
  kids,
  value,
  onChange,
  allLabel = "Everyone",
}: {
  kids: MemberDTO[];
  value: string[];
  onChange: (ids: string[]) => void;
  allLabel?: string;
}) {
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onChange([])}
        aria-pressed={value.length === 0}
        className={cx(
          "rounded-full px-3 py-1.5 text-sm font-bold transition",
          value.length === 0 ? "bg-brand text-on-brand" : "bg-sunken text-muted ring-1 ring-line",
        )}
      >
        {allLabel}
      </button>
      {kids.map((k) => (
        <button
          key={k.id}
          type="button"
          onClick={() => toggle(k.id)}
          aria-pressed={value.includes(k.id)}
          className={cx(
            "inline-flex items-center gap-1.5 rounded-full py-1 pl-1 pr-3 text-sm font-bold transition",
            value.includes(k.id) ? "bg-brand text-on-brand" : "bg-sunken text-muted ring-1 ring-line",
          )}
        >
          <span className="flex size-6 items-center justify-center rounded-full bg-card text-base">{k.avatar}</span>
          {k.name}
        </button>
      ))}
    </div>
  );
}

export function DayPicker({ value, onChange }: { value: number; onChange: (mask: number) => void }) {
  return (
    <div className="flex gap-1.5">
      {DAY_NAMES.map((d, i) => {
        const on = dayInMask(value, i);
        return (
          <button
            key={d}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value & ~(1 << i) : value | (1 << i))}
            className={cx(
              "h-10 flex-1 rounded-xl text-xs font-extrabold transition",
              on ? "bg-brand text-on-brand" : "bg-sunken text-muted ring-1 ring-line",
            )}
          >
            {d.slice(0, 2)}
          </button>
        );
      })}
    </div>
  );
}

const CONFETTI_COLORS = ["#f29e1b", "#14b8a6", "#8b5cf6", "#f43f5e", "#0ea5e9", "#84cc16"];

/** A short celebratory burst. Mount it with a changing key to replay. */
export function Confetti({ pieces = 36 }: { pieces?: number }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 2200);
    return () => clearTimeout(t);
  }, []);
  if (!visible || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[90] overflow-hidden" aria-hidden>
      {Array.from({ length: pieces }).map((_, i) => (
        <span
          key={i}
          className="absolute top-0 block h-3 w-2 rounded-sm"
          style={
            {
              left: `${(i * 97) % 100}%`,
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animation: `confetti-fall ${1.4 + ((i * 13) % 10) / 10}s ease-in ${((i * 7) % 10) / 20}s forwards`,
              "--dx": `${((i * 37) % 80) - 40}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
